import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import { useTranslation } from "react-i18next";

import {
  MAX_TAX_PERCENTAGE,
  MIN_TAX_PERCENTAGE,
  TAX_COUNTRIES,
} from "../../../../constants/monetization";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  label: {
    fontSize: 14,
    color: "#666666",
  },
  select: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  helper: {
    fontSize: "0.75rem",
    color: "#697386",
  },
  warning: {
    fontSize: "0.75rem",
    color: "#DC2626",
  },
  addButton: {
    alignSelf: "flex-start",
    textTransform: "none",
    color: "#5E77FF",
    fontWeight: 600,
    padding: 0,
  },
  addForm: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    border: "1px solid #E0E0E0",
  },
  addField: {
    backgroundColor: "white",
    flex: "1 1 140px",
  },
  addActions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  confirmDialog: {
    padding: theme.spacing(4),
    maxWidth: 460,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  confirmTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1a1f36",
  },
  confirmText: {
    fontSize: "0.9rem",
    color: "#697386",
  },
  confirmActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  primaryButton: {
    backgroundColor: "#5E77FF",
    color: "white",
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#4A5FD1",
    },
  },
  secondaryButton: {
    textTransform: "none",
    borderRadius: 8,
    color: "#697386",
  },
}));

const TaxRateSelector = ({
  taxRateOptions,
  taxRateOptionId,
  onTaxRateChange,
  isAddFormOpen,
  onToggleAddForm,
  newTaxRate,
  onNewTaxRateChange,
  onSubmitNewTaxRate,
  isCreatingTaxRate,
  isConfirmOpen,
  onConfirmCreate,
  onCancelConfirm,
  showMissingTaxWarning,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const percentageOutOfRange =
    newTaxRate.percentage === "" ||
    isNaN(Number(newTaxRate.percentage)) ||
    Number(newTaxRate.percentage) < MIN_TAX_PERCENTAGE ||
    Number(newTaxRate.percentage) > MAX_TAX_PERCENTAGE;

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>
        {t("tax_rate.label_required")}
      </Typography>
      <TextField
        select
        value={taxRateOptionId === null ? "" : taxRateOptionId}
        onChange={onTaxRateChange}
        variant="outlined"
        required
        className={classes.select}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="" disabled>
          {t("tax_rate.placeholder")}
        </MenuItem>
        {taxRateOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.displayName}
            {option.OwnerUserId ? ` ${t("tax_rate.private_suffix")}` : ""}
          </MenuItem>
        ))}
      </TextField>
      {showMissingTaxWarning ? (
        <Typography className={classes.warning}>
          {t("tax_rate.required_to_activate")}
        </Typography>
      ) : (
        <Typography className={classes.helper}>{t("tax_rate.helper")}</Typography>
      )}

      {!isAddFormOpen ? (
        <Button
          className={classes.addButton}
          startIcon={<AddIcon />}
          onClick={onToggleAddForm}
        >
          {t("tax_rate.add_rate")}
        </Button>
      ) : (
        <Box className={classes.addForm}>
          <TextField
            select
            name="country"
            label={t("tax_rate.country")}
            value={newTaxRate.country}
            onChange={onNewTaxRateChange}
            variant="outlined"
            size="small"
            className={classes.addField}
          >
            {TAX_COUNTRIES.map((code) => (
              <MenuItem key={code} value={code}>
                {t(`tax_rate.countries.${code}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            name="percentage"
            label={t("tax_rate.percentage")}
            value={newTaxRate.percentage}
            onChange={onNewTaxRateChange}
            variant="outlined"
            size="small"
            type="number"
            inputProps={{
              min: MIN_TAX_PERCENTAGE,
              max: MAX_TAX_PERCENTAGE,
              step: 0.1,
            }}
            InputProps={{ endAdornment: "%" }}
            error={newTaxRate.percentage !== "" && percentageOutOfRange}
            helperText={
              newTaxRate.percentage !== "" && percentageOutOfRange
                ? t("tax_rate.percentage_range_error")
                : ""
            }
            className={classes.addField}
          />
          <Box className={classes.addActions}>
            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={onSubmitNewTaxRate}
              disabled={isCreatingTaxRate || percentageOutOfRange}
            >
              {isCreatingTaxRate ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                t("tax_rate.create")
              )}
            </Button>
            <Button className={classes.secondaryButton} onClick={onToggleAddForm}>
              {t("tax_rate.cancel")}
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={isConfirmOpen} onClose={onCancelConfirm}>
        <Box className={classes.confirmDialog}>
          <Typography className={classes.confirmTitle}>
            {t("tax_rate.confirm_title")}
          </Typography>
          <Typography className={classes.confirmText}>
            {t("tax_rate.confirm_text", {
              percentage: newTaxRate.percentage,
            })}
          </Typography>
          <Box className={classes.confirmActions}>
            <Button className={classes.secondaryButton} onClick={onCancelConfirm}>
              {t("tax_rate.cancel")}
            </Button>
            <Button
              variant="contained"
              className={classes.primaryButton}
              onClick={onConfirmCreate}
              disabled={isCreatingTaxRate}
            >
              {isCreatingTaxRate ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                t("tax_rate.confirm_button")
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default TaxRateSelector;
